import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { TableCell, TableRow, useTheme } from "@mui/material";
import { flexRender, Row } from "@tanstack/react-table";
import { CSSProperties } from "react";
import { ReleaseTrack } from "../../../store/models/release";
import { isScheduledSimple } from "../../../utils/releases/dateUtils";
import { ReleaseTrackTableRowId } from "./defaultColumnsForReleaseTrackTable";

interface DraggableArtistReleaseTrackTableRowProps {
  row: Row<ReleaseTrack>;
  unitPrice?: string | null;
  selectedProductId?: number;
  isArtistOnRelease?: boolean;
  openFeedbackModal?: () => void;
  lowestUnitPriceProductId?: number;
  isCreateMode?: boolean;
  isDesktop: boolean;
  releaseIsStreamEnabled: boolean;
  currency?: string;
  productPublishedAt?: string | null;
  productPublishedEnd?: string | null;
}

export const DraggableArtistReleaseTrackTableRow = ({
  row,
  unitPrice,
  currency,
  selectedProductId,
  isArtistOnRelease,
  openFeedbackModal,
  lowestUnitPriceProductId,
  isCreateMode,
  isDesktop,
  releaseIsStreamEnabled,
  productPublishedAt,
  productPublishedEnd,
}: DraggableArtistReleaseTrackTableRowProps) => {
  const theme = useTheme();
  const { transform, transition, setNodeRef, isDragging } = useSortable({
    id: row.original.id,
  });
  const isProductScheduled = isScheduledSimple(
    productPublishedAt || null,
    productPublishedEnd || null,
  );
  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition: transition,
    opacity: isDragging ? 0.8 : 1,
    zIndex: isDragging ? 1 : 0,
    position: "relative",
    justifyContent: "flex-start",
    flexDirection: "row",
    borderBottom: `1px solid ${theme.palette.divider}`,
  };
  return (
    <TableRow ref={setNodeRef} style={style} key={row.id}>
      {row.getVisibleCells().map((cell) => {
        const cellContent = flexRender(cell.column.columnDef.cell, {
          ...cell.getContext(),
          unitPrice,
          currency,
          selectedProductId,
          isArtistOnRelease,
          openFeedbackModal,
          lowestUnitPriceProductId,
          isCreateMode,
          isDesktop,
          releaseIsStreamEnabled,
          productPublishedAt,
          isProductScheduled,
        });

        return (
          <TableCell
            key={cell.id}
            sx={(theme) => ({
              flex: 1,
              borderBottom: `1px solid ${theme.palette.divider}`,
              maxWidth: cell.column.getSize(),
              // ReorderTab doesn't need that much padding
              ...(cell.column.id === ReleaseTrackTableRowId.ReorderTab && {
                padding: 0,
              }),
              // PlaySection doesn't need that much padding on mobile
              ...(cell.column.id === ReleaseTrackTableRowId.PlaySection &&
                !isDesktop && {
                  padding: 0,
                }),
            })}
          >
            {cellContent}
          </TableCell>
        );
      })}
    </TableRow>
  );
};
